import {
  Anchor,
  Box,
  Center,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { toTitleCase, utc } from '@vrplatform/utils';
import { useMemo } from 'react';
import type { AuditEffectRow } from './useAuditEffectsTableQuery';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import { AUDIT_EFFECT_ENTITY_TYPES } from './useAuditEffectsTableFilter';
import {
  useDepositDetailDrawer,
  useExpenseDetailDrawer,
  useReservationDetailDrawer,
  usePayoutDetailDrawer,
  useBankTransferDetailDrawer,
} from '../../drawers';
import { useAuditEffectsModal } from './useAuditEffectsModal';

export const useAuditEffectsTableColumns = () => {
  'use memo';

  return useMemo<MRT_ColumnDef<AuditEffectRow>[]>(
    () => [
      {
        header: 'Execution',
        id: 'execution',
        accessorFn: ({ claimedAt, completedAt, lastAttemptAt }) =>
          claimedAt ?? completedAt ?? lastAttemptAt ?? '',
        Cell: ({ row }) => {
          const { claimedAt, completedAt, lastAttemptAt } = row.original;

          const formatDate = (value?: string | null) =>
            value ? utc(value).format('MMM D, YYYY HH:mm') : '—';

          const rows: Array<[string, string | null | undefined]> = [
            ['Claimed', claimedAt],
            ['Completed', completedAt],
            ['Last Attempt', lastAttemptAt],
          ];

          return (
            <Stack gap="xs" align="flex-start">
              {rows.map(([label, value]) => (
                <Text key={label} component="p" m={0} c="gray.6">
                  <Text component="span" fw={500} c="gray.8">
                    {label}:
                  </Text>{' '}
                  {formatDate(value)}
                </Text>
              ))}
            </Stack>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ row }) => {
          const { status, lastError } = row.original;

          return (
            <Stack gap="xs" align="flex-start">
              <Text size="sm" component="p" m={0} fw={500}>
                {status ?? '—'}
              </Text>
              {lastError ? (
                <Text
                  size="xs"
                  component="p"
                  m={0}
                  c="gray.6"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {lastError}
                </Text>
              ) : null}
            </Stack>
          );
        },
      },
      {
        header: 'Effect Type',
        accessorFn: (row) => row.effectType || '-',
      },
      {
        header: 'Entity',
        id: 'entity',
        Cell: ({ row }) => {
          const { entity } = row.original;
          const { close: closeModal } = useAuditEffectsModal();

          const { open: openReservation } = useReservationDetailDrawer();
          const { open: openDeposit } = useDepositDetailDrawer();
          const { open: openExpense } = useExpenseDetailDrawer();
          const { open: openPayout } = usePayoutDetailDrawer();
          const { open: openTransfer } = useBankTransferDetailDrawer();

          if (entity?.type === 'reservation') {
            return (
              <EntityWrapper type={entity.type}>
                <Anchor
                  onClick={() => {
                    closeModal();
                    openReservation(entity.data.id);
                  }}
                >
                  {entity.data.confirmationCode}
                </Anchor>
                <Text size="xs" c="gray">
                  {[entity.data.checkIn, entity.data.checkOut]
                    .filter(Boolean)
                    .join(' - ')}
                </Text>
              </EntityWrapper>
            );
          }

          if (
            entity?.type === 'recurringTransactionTemplate' ||
            entity?.type === 'transaction'
          ) {
            const onClick = () => {
              closeModal();
              if (entity.data.type === 'expense') {
                openExpense(entity.data.id);
              } else if (entity.data.type === 'deposit') {
                openDeposit(entity.data.id);
              } else if (entity.data.type === 'payout') {
                openPayout(entity.data.id);
              } else if (entity.data.type === 'transfer') {
                openTransfer(entity.data.id);
              }
            };

            return (
              <EntityWrapper type={entity.type}>
                <Anchor onClick={onClick}>{entity.data.uniqueRef}</Anchor>
                <Text size="xs" c="gray">
                  {entity.data.date}
                </Text>
              </EntityWrapper>
            );
          }

          if (entity) {
            const entries: Array<[string, string | null | undefined]> = [
              ['Type', toTitleCase(entity?.type)],
              ['ID', entity?.data.id],
            ];

            return (
              <EntityWrapper type={entity.type}>
                {entries.map(([label, value]) => (
                  <Text key={label} component="p" m={0} c="gray.6">
                    <Text component="span" fw={500} c="gray.8">
                      {label}:
                    </Text>{' '}
                    {value ?? '—'}
                  </Text>
                ))}
              </EntityWrapper>
            );
          }

          return null;
        },
      },
    ],
    []
  );
};

type EntityType = NonNullable<AuditEffectRow['entity']>['type'];

const icons: Record<EntityType, IconDefinition> = {
  other: 'Activity2Icon',
  recurringTransactionTemplate: 'RefreshCwIcon',
  reservation: 'CalendarEventIcon',
  transaction: 'CircleDollarIcon',
};

const EntityWrapper = ({
  children,
  type,
}: {
  children: React.ReactNode;
  type: EntityType | undefined;
}) => {
  return (
    <Group gap="sm" wrap="nowrap">
      <Tooltip
        label={
          AUDIT_EFFECT_ENTITY_TYPES[type || '-'] || toTitleCase(type || '-')
        }
      >
        <Center>
          <Icon icon={icons[type || '-'] || 'Activity2Icon'} size={18} />
        </Center>
      </Tooltip>
      <Box>{children}</Box>
    </Group>
  );
};
